import React, { useCallback, useMemo, useRef, useState } from "react";
import { Drawer, Spin } from "antd";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import BarChartWrapper from "components/Dashboard/BarChartWrapper";
import { apiRequest } from "util/services";
import WelcomeDrawerReviewsList from "pages/Welcome/WelcomeDrawerReviewsList";
import { useWelcomeReviewsDrawerCloseEffect } from "pages/Welcome/useWelcomeReviewsDrawerCloseEffect";
import {
  WELCOME_REVIEWS_DRAWER_SHARED_PROPS,
  welcomeReviewsDrawerWidth,
} from "pages/Welcome/welcomeReviewsDrawerProps";
import {
  computeMixedInnerTooltipBreakdown,
  resolveMixedInnerTooltipSource,
} from "util/mixedInnerTooltip";

const PAGE_SIZE = 10;

const norm = (s) => String(s || "").trim().toLowerCase();

const SENTIMENT_BY_DATASET_INDEX = [
  "negative",
  "neutral",
  "positive",
  "unspecified",
];

/**
 * Welcome metric 14 payload → rows for dashboard BarChartWrapper (same chart as main dashboard).
 * Bars are sorted by review count descending (left = highest), when topic_review_counts has any positive value.
 */
export function welcomeMetric14StackedBarToBarChartData(tp) {
  if (!tp || tp.chart_type !== "stacked_bar") return [];
  const labels = tp.labels || [];
  if (!labels.length) return [];
  const datasets = tp.datasets || [];
  const getVals = (name) => {
    const ds = datasets.find((d) => norm(d.label) === name);
    return Array.isArray(ds?.data) ? ds.data : [];
  };
  const pos = getVals("positive");
  const neu = getVals("neutral");
  const neg = getVals("negative");
  const counts = Array.isArray(tp.topic_review_counts) ? tp.topic_review_counts : null;
  const topicLibIds = Array.isArray(tp.topic_lib_ids) ? tp.topic_lib_ids : null;
  const dashTopicIds = Array.isArray(tp.dashboard_topic_ids)
    ? tp.dashboard_topic_ids
    : null;
  const mixedInnerTooltipRows = Array.isArray(tp.mixed_inner_tooltip)
    ? tp.mixed_inner_tooltip
    : null;

  const rows = labels.map((label, i) => {
    const mixedSource = resolveMixedInnerTooltipSource(
      mixedInnerTooltipRows?.[i],
    );
    const mixedInnerBreakdown = computeMixedInnerTooltipBreakdown(
      mixedSource.mixedReviewCount,
      mixedSource.entries,
      { excludeLabels: ["mixed"] },
    );
    const row = {
      label,
      id: `welcome-ts-${i}`,
      child: null,
      count: counts && counts[i] != null ? Number(counts[i]) : 0,
      topicLibId:
        topicLibIds && topicLibIds[i] != null && Number.isFinite(Number(topicLibIds[i]))
          ? Number(topicLibIds[i])
          : null,
      dashboardTopicId:
        dashTopicIds &&
        dashTopicIds[i] != null &&
        Number.isFinite(Number(dashTopicIds[i]))
          ? Number(dashTopicIds[i])
          : null,
      sentiment: {
        "1.0": { percentage: pos[i] ?? 0 },
        "0.5": { percentage: neu[i] ?? 0 },
        "0.0": { percentage: neg[i] ?? 0 },
        "-1": { percentage: 0 },
      },
      mixedPercentage: mixedSource.mixedPercentage,
      mixedInnerBreakdown,
    };
    return row;
  });

  const hasComparableCounts = rows.some((r) => r.count > 0);
  if (hasComparableCounts) {
    rows.sort((a, b) => {
      if (b.count !== a.count) return b.count - a.count;
      return String(a.label).localeCompare(String(b.label), undefined, {
        sensitivity: "base",
      });
    });
  }

  return rows.map((row, j) => ({
    ...row,
    id: `welcome-ts-${j}`,
  }));
}

/**
 * Sentiment per Topic (metric 14) using the same BarChartWrapper as the product dashboard.
 * Click a colored segment to open reviews in a drawer.
 *
 * @param {number} widgetId
 * @param {number} [reportId] — when set, uses AI report org-scoped API
 */
export default function WelcomeTopicSentimentBarChart({
  payload,
  widgetId,
  reportId,
}) {
  const { t } = useTranslation();
  const theme = useSelector((state) => state.DashboardReducer.theme);
  const data = useMemo(
    () => welcomeMetric14StackedBarToBarChartData(payload),
    [payload],
  );
  const [selectedTopics, setSelectedTopics] = useState({});
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [drawerTitle, setDrawerTitle] = useState({ line1: "", line2: "" });
  const [reviewList, setReviewList] = useState([]);
  const [reviewTotal, setReviewTotal] = useState(0);
  const [reviewPage, setReviewPage] = useState(1);
  const segmentRef = useRef(null);

  const handleDrawerClose = useCallback(() => {
    setDrawerOpen(false);
    setReviewsLoading(false);
    setReviewList([]);
    setReviewTotal(0);
    setReviewPage(1);
    segmentRef.current = null;
    setDrawerTitle({ line1: "", line2: "" });
  }, []);

  useWelcomeReviewsDrawerCloseEffect(drawerOpen, handleDrawerClose);

  const sentimentLabel = useCallback(
    (key) => {
      if (key === "positive") return t("feedback_dropdown_values.2");
      if (key === "neutral") return t("feedback_dropdown_values.3");
      if (key === "negative") return t("feedback_dropdown_values.4");
      return key;
    },
    [t],
  );

  const requestSegmentReviews = useCallback(
    async (pageNum, segment, { pageSize = PAGE_SIZE, maxReviews = 100 } = {}) => {
      const row = segment?.row;
      const sentimentKey = segment?.sentimentKey;
      if (!widgetId || !row || !sentimentKey) {
        return { reviews: [], total: 0 };
      }

      const topicLibId = row.topicLibId;
      const dashboardTopicId = row.dashboardTopicId;
      if (topicLibId == null && dashboardTopicId == null) {
        return { reviews: [], total: 0 };
      }

      const body = {
        widget_id: widgetId,
        sentiment_segment: sentimentKey,
        max_reviews: maxReviews,
        page: pageNum,
        page_size: pageSize,
      };
      if (topicLibId != null) body.topic_lib_id = topicLibId;
      if (dashboardTopicId != null)
        body.dashboard_topic_id = dashboardTopicId;

      const url =
        reportId != null
          ? `welcome/ai-services/reports/${reportId}/widget-metric-14-sentiment-segment-reviews`
          : "welcome/widget/metric-14/reviews";

      const response = await apiRequest(url, "POST", body, true);
      const reviews = Array.isArray(response?.success) ? response.success : [];
      const total =
        response?.total != null && Number.isFinite(Number(response.total))
          ? Number(response.total)
          : reviews.length;
      return { reviews, total };
    },
    [widgetId, reportId],
  );

  const loadSegmentReviewsPage = useCallback(
    async (pageNum, segment) => {
      setReviewsLoading(true);
      try {
        const { reviews, total } = await requestSegmentReviews(
          pageNum,
          segment,
        );
        setReviewList(reviews);
        setReviewTotal(total);
      } catch {
        setReviewList([]);
        setReviewTotal(0);
      } finally {
        setReviewsLoading(false);
      }
    },
    [requestSegmentReviews],
  );

  const loadAllReviewsForExport = useCallback(async () => {
    const segment = segmentRef.current;
    if (!segment) return reviewList;
    if (reviewTotal > 0 && reviewList.length >= reviewTotal) {
      return reviewList;
    }
    const pageSize = Math.min(Math.max(reviewTotal || PAGE_SIZE, PAGE_SIZE), 100);
    const { reviews } = await requestSegmentReviews(1, segment, {
      pageSize,
      maxReviews: 100,
    });
    return reviews.length ? reviews : reviewList;
  }, [requestSegmentReviews, reviewList, reviewTotal]);

  const fetchSegmentReviews = useCallback(
    async (barIndex, datasetIndex) => {
      if (!widgetId || !data?.length) return;
      const sentimentKey = SENTIMENT_BY_DATASET_INDEX[datasetIndex];
      if (!sentimentKey || sentimentKey === "unspecified") return;

      const row = data[barIndex];
      if (!row) return;

      const topicLibId = row.topicLibId;
      const dashboardTopicId = row.dashboardTopicId;
      if (topicLibId == null && dashboardTopicId == null) return;

      const segment = { row, sentimentKey };
      segmentRef.current = segment;

      setDrawerTitle({
        line1: row.label || "",
        line2: sentimentLabel(sentimentKey),
      });
      setDrawerOpen(true);
      setReviewPage(1);
      setReviewList([]);
      setReviewTotal(0);
      await loadSegmentReviewsPage(1, segment);
    },
    [widgetId, data, sentimentLabel, loadSegmentReviewsPage],
  );

  const handleReviewPageChange = useCallback(
    async (p) => {
      setReviewPage(p);
      await loadSegmentReviewsPage(p, segmentRef.current);
    },
    [loadSegmentReviewsPage],
  );

  if (!data.length) return null;

  return (
    <>
      <BarChartWrapper
        data={data}
        selectedTopics={selectedTopics}
        setSelectedTopics={setSelectedTopics}
        metric="sentiment"
        stacked={true}
        disableSelection={true}
        onlyOneMetric={false}
        scrollable={true}
        onSentimentStackSegmentClick={fetchSegmentReviews}
      />
      <Drawer
        title={drawerTitle.line1 || t("common.reviews", "Reviews")}
        extra={
          <button
            type="button"
            className="welcome-reviews-drawer-header-close"
            onClick={handleDrawerClose}
          >
            {t("common.close", "Close")}
          </button>
        }
        {...WELCOME_REVIEWS_DRAWER_SHARED_PROPS}
        onClose={handleDrawerClose}
        open={drawerOpen}
        width={welcomeReviewsDrawerWidth()}
      >
        {drawerTitle.line2 ? (
          <p
            style={{
              marginTop: 0,
              marginBottom: 16,
              fontSize: 12,
              color: theme === "dark" ? "#8c8c8c" : "#666",
              wordBreak: "break-word",
            }}
          >
            {drawerTitle.line2}
          </p>
        ) : null}
        <Spin spinning={reviewsLoading}>
          <WelcomeDrawerReviewsList
            allReviews={reviewList}
            reviewsLoading={reviewsLoading}
            serverSide
            total={reviewTotal}
            currentPage={reviewPage}
            pageSize={PAGE_SIZE}
            onServerPageChange={handleReviewPageChange}
            showTotalCount
            loadAllReviewsForExport={loadAllReviewsForExport}
            exportFileLabel={[drawerTitle.line1, drawerTitle.line2]
              .filter(Boolean)
              .join(" · ")}
          />
        </Spin>
      </Drawer>
    </>
  );
}
